/* iter-72c — Grouped drawer/dialog mounts for EditorPanel.
 *
 * Pulled out of `EditorPanel.jsx` (previously ~750 lines) so the main
 * panel body focuses on state, effects, and the manuscript surface.
 * Every prop maps 1:1 to the previous inline JSX — no behavioural
 * change is introduced by this extraction.
 *
 * Owner-only gating (Plagiarism, Suggestions accept/decline) and
 * silent project reloads (`onChapterChanged`, `onRestored`) live in
 * the parent so this file stays a pure mount surface.
 */
import React from 'react';
import { VersionHistoryDrawer }  from './VersionHistoryDrawer';
import { PlagiarismDrawer }      from './PlagiarismDrawer';
import { SpellCheckDrawer }      from './SpellCheckDrawer';
import ChapterBibleDialog        from '../../dialogs/ChapterBibleDialog';
import EditorSearchDialog        from '../../dialogs/EditorSearchDialog';
import BookmarksDialog           from '../../dialogs/BookmarksDialog';
import SuggestionsDrawer         from './SuggestionsDrawer';
import SelectionActionPopover    from './SelectionActionPopover';
import { chaptersApi } from '@/lib/api';

export default function EditorDrawers({
  // ids + auth
  project,
  chapter,
  user,
  canMutate,
  // shared refs
  scrollAreaRef,
  // drawer open state (values)
  historyOpen,
  plagiarismOpen,
  spellOpen,
  bibleOpen,
  searchOpen,
  bookmarksOpen,
  suggestionsOpen,
  // drawer open state (setters)
  setHistoryOpen,
  setPlagiarismOpen,
  setSpellOpen,
  setBibleOpen,
  setSearchOpen,
  setBookmarksOpen,
  setSuggestionsOpen,
  // spell-check payload
  spellIssues,
  // bookmarks — selection pre-fill
  pendingBookmarkSel,
  setPendingBookmarkSel,
  // suggestions refresh key (bump to force drawer refetch)
  suggestionsRefreshKey,
  // callbacks up to the parent
  setActiveChapterId,
  loadProject,
  onSuggestionCreated,
  onSuggestionResolved,
}) {
  const projectId = project?.id;
  const chapterId = chapter?.id;
  const isOwner = Boolean(user?.user_id) && project?.user_id === user.user_id;
  const reloadProject = async () => { if (projectId) await loadProject(projectId, { silent: true }); };

  return (
    <>
      <VersionHistoryDrawer
        chapter={chapter}
        projectId={projectId}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        canMutate={canMutate}
        onRestored={reloadProject}
      />

      <PlagiarismDrawer
        open={plagiarismOpen}
        onClose={() => setPlagiarismOpen(false)}
        project={project}
        chapter={chapter}
        isOwner={isOwner}
        canMutate={canMutate}
        onChapterChanged={reloadProject}
      />

      <SpellCheckDrawer
        open={spellOpen}
        onClose={() => setSpellOpen(false)}
        issues={spellIssues}
        chapter={chapter}
        onChapterChanged={reloadProject}
      />

      <ChapterBibleDialog
        open={bibleOpen}
        onOpenChange={setBibleOpen}
        projectId={projectId}
        onSaved={reloadProject}
      />

      <EditorSearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        projectId={projectId}
        chapterId={chapterId}
        chapterContent={chapter?.content || ''}
        onJumpToChapter={(cid) => { if (cid) setActiveChapterId(cid); }}
        onReplaceInChapter={async (nextContent) => {
          if (!chapter?.id) return;
          const updated = await chaptersApi.update(chapter.id, { content: nextContent });
          if (reloadProject) await reloadProject();
          return updated;
        }}
      />

      <BookmarksDialog
        open={bookmarksOpen}
        onOpenChange={setBookmarksOpen}
        projectId={projectId}
        currentChapterId={chapterId}
        currentPageNumber={chapter?.number || 1}
        currentSelection={pendingBookmarkSel}
        currentUserId={user?.user_id}
        onJumpToChapter={(cid) => { if (cid) setActiveChapterId(cid); }}
      />

      <SuggestionsDrawer
        key={suggestionsRefreshKey}
        open={suggestionsOpen}
        onClose={() => setSuggestionsOpen(false)}
        projectId={projectId}
        chapterId={chapterId}
        currentUserId={user?.user_id}
        isOwner={isOwner}
        onChapterChanged={async () => {
          await reloadProject();
          onSuggestionResolved?.();
        }}
      />

      <SelectionActionPopover
        scrollAreaRef={scrollAreaRef}
        projectId={projectId}
        chapterId={chapterId}
        onBookmark={(sel) => { setPendingBookmarkSel(sel); setBookmarksOpen(true); }}
        onSuggestionCreated={() => {
          onSuggestionCreated?.();
          setSuggestionsOpen(true);
        }}
      />
    </>
  );
}
